import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import TerminalIcon from "@mui/icons-material/Terminal";
import SyncIcon from "@mui/icons-material/Sync";
import { useUI } from "../../Context/UIContext";
import { usePermissions } from "../../Context/permissionsProvider";
import { useVendorIndexPolling } from "../../Context/vendorIndexPollingContext";
import CatalogMaintenanceShell from "./CatalogMaintenanceShell";
import { buildVendorBusinessTradeTitle } from "./VendorBusinessTradeNames";
import CatalogTablePagination from "./CatalogTablePagination";
import CatalogVendorSummary from "./CatalogVendorSummary";
import CatalogTabStatusMessage from "./CatalogTabStatusMessage";
import RawFeedTab from "./RawFeedTab";
import RawMetadataTab from "./RawMetadataTab";
import MappingPreviewMatrix from "./MappingPreviewMatrix";
import MappingTokenStatusBar from "./MappingTokenStatusBar";
import CatalogDbOutputView from "./CatalogDbOutputView";
import ProductCodeRegenPanel from "./ProductCodeRegenPanel";
import CatalogImportLogDialog from "./CatalogImportLogDialog";
import LegendDialog from "./LegendDialog";
import RowSkipRulesPanel from "./RowSkipRulesPanel";
import ColumnDefaultsPanel from "./ColumnDefaultsPanel";
import ScheduledImportToggle from "./ScheduledImportToggle";
import GoToLinePanel from "./GoToLinePanel";
import useCatalogImportJob from "./hooks/useCatalogImportJob";
import {
  deleteNormalizedRow,
  getCatalogFieldKeys,
  getCatalogMapping,
  getCatalogOutput,
  getCatalogVendor,
  previewCatalogFeed,
  saveCatalogMapping,
  saveCatalogCell,
  saveNormalizedCell,
  bulkCategorizeByAlbero,
  hardDeleteCatalogRows,
} from "./services/catalogMappingService";
import {
  buildMappingLegendItems,
  FEED_FORMAT_LEGEND,
  FEED_FORMAT_LEGEND_TITLE,
  IMPORT_STATUS_LEGEND,
  IMPORT_STATUS_LEGEND_TITLE,
  IMPORT_ISSUES_LEGEND,
  IMPORT_ISSUES_LEGEND_TITLE,
  GLOBAL_IMPORT_ERRORS_LEGEND,
  GLOBAL_IMPORT_WARNINGS_LEGEND,
  GLOBAL_IMPORT_ISSUES_LEGEND_TITLE,
  MAPPING_VALIDATION_LEGEND,
  MAPPING_VALIDATION_LEGEND_TITLE,
  MAPPING_STATE_LEGEND,
  MAPPING_STATE_LEGEND_TITLE,
  MAPPING_TOKEN_LEGEND_TITLE,
} from "./utils/legendsContent";
import {
  DEFAULT_SOURCE_TYPE_CONFIG,
  parseSourceTypeConfig,
  withManualDetection,
} from "./utils/sourceTypeConfig";
import { buildMappingRulesPayload, normalizeRowSkipRules, normalizeScheduledEnabled } from "./utils/mappingRulesUtils";
import { normalizeColumnDefaultGroups } from "./utils/columnDefaultRulesUtils";
import { resolveMappingTabStatus } from "./utils/tabStatusMessages";
import { formatHardDeleteRulesLabel } from "./utils/catalogFieldLabels";
import {
  createEmptyColumnFilter,
  defaultCatalogColumnFilters,
} from "./utils/columnFilterUtils";
import { CM_TOOLTIPS } from "./utils/tooltipsContent";
import {
  cycleMappingProblemFilter,
  mappingImportProblemsAvailable,
  mappingProblemFilterButtonLabel,
  mappingProblemFilterIsActive,
  mappingProblemFilterTooltip,
  MAPPING_PROBLEM_FILTER,
} from "./utils/mappingProblemFilterUtils";

const emptyMappingMeta = () => ({
  mapping_status: "none",
  last_run_state: "",
  last_run_summary: {},
  import_errors_available: false,
  updated_at: "",
});

const emptyRawMetadata = () => ({
  feed_config: {},
  mapping_config: {},
  last_run_summary: {},
});

const emptyPreview = () => ({
  rows: [],
  total_rows: 0,
  column_count: 0,
  header_labels: {},
});

const CatalogMappingEditor = () => {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const { showError, showSuccess } = useUI();
  const { isActionVisible } = usePermissions();
  const canMapping = isActionVisible("manutenzioneCataloghi", "mapping");
  const canImport = isActionVisible("manutenzioneCataloghi", "import");
  const canCatalogo = isActionVisible("manutenzioneCataloghi", "catalogo");
  const canProductCodeRegen = isActionVisible("manutenzioneCataloghi", "productCodeRegen");
  const canBulkCategorize = isActionVisible("manutenzioneCataloghi", "bulkCategorize");
  const canVendorSchedule = isActionVisible("manutenzioneCataloghi", "vendorSchedule");
  const {
    isVendorReindexRunning,
    startVendorIndexPolling,
    triggerVendorReindex,
  } = useVendorIndexPolling();
  const [reindexSubmitting, setReindexSubmitting] = useState(false);

  const [tab, setTab] = useState(0);
  const [pageLoading, setPageLoading] = useState(true);
  const [savingMapping, setSavingMapping] = useState(false);
  const [persistingConfig, setPersistingConfig] = useState(false);
  const [savingRawMetadata, setSavingRawMetadata] = useState(false);
  const [vendor, setVendor] = useState(null);
  const [fieldKeys, setFieldKeys] = useState([]);
  const [mapping, setMapping] = useState({});
  const [rowSkipRules, setRowSkipRules] = useState([]);
  const [skipShowMatchesOnly, setSkipShowMatchesOnly] = useState(false);
  const [defShowMatchesOnly, setDefShowMatchesOnly] = useState(false);
  const [scheduledEnabled, setScheduledEnabled] = useState(true);
  const [columnDefaults, setColumnDefaults] = useState({});
  const [mappingMeta, setMappingMeta] = useState(emptyMappingMeta);
  const [rawMetadata, setRawMetadata] = useState(emptyRawMetadata);
  const [sourceTypeConfig, setSourceTypeConfig] = useState(DEFAULT_SOURCE_TYPE_CONFIG);
  const [issues, setIssues] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [preview, setPreview] = useState(null);
  const [catalogPage, setCatalogPage] = useState({
    total: 0,
    rows: [],
    product_columns: [],
    catalog_columns: [],
    error_columns: [],
    error_rows: 0,
    source: "csv",
  });
  const [catalogFilters, setCatalogFilters] = useState({
    uncategorizedOnly: false,
    isDisattivato: "",
    isDeleted: "",
    certificato: "",
    stato: "",
    productCodeFilter: "",
    columnFilters: defaultCatalogColumnFilters(),
  });
  const [mappingPage, setMappingPage] = useState(0);
  const [mappingRowsPerPage, setMappingRowsPerPage] = useState(20);
  const [scrollTargetFeedRow, setScrollTargetFeedRow] = useState(null);
  const [mappingProblemFilter, setMappingProblemFilter] = useState(MAPPING_PROBLEM_FILTER.ALL);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [catalogLoadError, setCatalogLoadError] = useState("");
  const [serviceError, setServiceError] = useState("");
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [legendOpen, setLegendOpen] = useState(false);
  const [mappingLegendOpen, setMappingLegendOpen] = useState(false);
  const [importLegendOpen, setImportLegendOpen] = useState(false);
  const [importIssuesLegendOpen, setImportIssuesLegendOpen] = useState(false);
  const [globalImportLegendOpen, setGlobalImportLegendOpen] = useState(false);
  const [validationLegendOpen, setValidationLegendOpen] = useState(false);
  const [formatLegendOpen, setFormatLegendOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [bulkCategorizeRules, setBulkCategorizeRules] = useState([]);
  const [executingBulkCategorize, setExecutingBulkCategorize] = useState(false);
  const [hardDeleteRules, setHardDeleteRules] = useState([]);
  const [hardShowMatchesOnly, setHardShowMatchesOnly] = useState(false);
  const [executingHardDelete, setExecutingHardDelete] = useState(false);

  const applyMappingData = useCallback((data, vendorData = null) => {
    setMapping(data?.column_mapping || {});
    setRowSkipRules(normalizeRowSkipRules(data?.row_skip || data?.mapping_config?.row_skip));
    setScheduledEnabled(normalizeScheduledEnabled(
      data?.mapping_config?.scheduled_enabled ?? data?.scheduled_enabled,
    ));
    setColumnDefaults(normalizeColumnDefaultGroups(data?.mapping_config?.column_defaults));
    setSourceTypeConfig(parseSourceTypeConfig(data?.feed_config, vendorData ?? vendor));
    setIssues(data?.issues || []);
    setWarnings(data?.warnings || []);
    setMappingMeta({
      mapping_status: data?.mapping_status || "none",
      last_run_state: data?.last_run_state || "",
      last_run_summary: data?.last_run_summary || {},
      import_errors_available: Boolean(data?.import_errors_available),
      updated_at: data?.updated_at || "",
    });
    setRawMetadata({
      feed_config: data?.feed_config ?? {},
      mapping_config: data?.mapping_config ?? {},
      last_run_summary: data?.last_run_summary ?? {},
    });
  }, [vendor]);

  const columnNumbers = useMemo(() => {
    const fromPreview = preview?.column_count
      ? Array.from({ length: preview.column_count }, (_, i) => String(i + 1))
      : [];
    const fromMapping = Object.keys(mapping || {});
    const merged = new Set([...fromPreview, ...fromMapping]);
    return Array.from(merged).sort((a, b) => Number(a) - Number(b));
  }, [preview, mapping]);

  const previewRows = useMemo(() => preview?.rows || [], [preview]);

  const totalMappingPages = useMemo(
    () => Math.max(1, Math.ceil((preview?.total_rows || 0) / mappingRowsPerPage)),
    [preview?.total_rows, mappingRowsPerPage],
  );

  const totalCatalogPages = useMemo(
    () => Math.max(1, Math.ceil((catalogPage.total || 0) / rowsPerPage)),
    [catalogPage.total, rowsPerPage],
  );

  const loadVendor = useCallback(async () => {
    const current = await getCatalogVendor(vendorId);
    setVendor(current || null);
    return current;
  }, [vendorId]);

  const loadMapping = useCallback(async (vendorData = null) => {
    const data = await getCatalogMapping(vendorId);
    applyMappingData(data, vendorData);
    return data;
  }, [vendorId, applyMappingData]);

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const data = await previewCatalogFeed(
        vendorId,
        mappingRowsPerPage,
        mappingPage * mappingRowsPerPage,
        mappingProblemFilter,
        {
          skipMatchesOnly: skipShowMatchesOnly,
          rowSkip: rowSkipRules,
          columnDefaultMatchesOnly: defShowMatchesOnly,
          columnDefaults,
        },
      );
      setPreview(data);
      setPreviewError("");
      if (typeof data?.import_errors_available === "boolean") {
        setMappingMeta((previous) => ({
          ...previous,
          import_errors_available: data.import_errors_available,
        }));
      }
      return data;
    } catch (error) {
      setPreview(emptyPreview());
      setPreviewError(error.message || "Errore nella preview del feed");
      return null;
    } finally {
      setPreviewLoading(false);
    }
  }, [vendorId, mappingPage, mappingRowsPerPage, mappingProblemFilter, skipShowMatchesOnly, rowSkipRules, defShowMatchesOnly, columnDefaults]);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const data = await getCatalogOutput(
        vendorId,
        page * rowsPerPage,
        rowsPerPage,
        false,
        catalogFilters,
        hardDeleteRules,
        hardShowMatchesOnly,
      );
      setCatalogPage({
        total: data?.total || 0,
        rows: data?.rows || [],
        product_columns: data?.product_columns || [],
        catalog_columns: data?.catalog_columns || [],
        error_columns: data?.error_columns || [],
        error_rows: data?.error_rows || 0,
        source: data?.source || "csv",
      });
      setCatalogLoadError("");
      return data;
    } catch (error) {
      setCatalogPage({
        total: 0,
        rows: [],
        product_columns: [],
        catalog_columns: [],
        error_columns: [],
        error_rows: 0,
        source: "csv",
      });
      setCatalogLoadError(error.message || "Errore nel caricamento del catalogo");
      return null;
    } finally {
      setCatalogLoading(false);
    }
  }, [vendorId, page, rowsPerPage, catalogFilters, hardDeleteRules, hardShowMatchesOnly]);

  const skipCatalogReloadRef = useRef(true);
  const skipPreviewReloadRef = useRef(true);

  const loadBaseData = useCallback(async () => {
    setServiceError("");
    try {
      const fields = await getCatalogFieldKeys();
      setFieldKeys(fields.fields || []);
    } catch (error) {
      setServiceError(error.message || "Servizio catalogo non disponibile");
    }

    try {
      const currentVendor = await loadVendor();
      await loadMapping(currentVendor);
    } catch (error) {
      setServiceError((previous) => previous || error.message || "Errore nel caricamento del vendor");
    }
  }, [loadVendor, loadMapping]);

  const loadAll = useCallback(async () => {
    setPageLoading(true);
    skipCatalogReloadRef.current = true;
    try {
      await loadBaseData();
      await loadCatalog();
      if (tab === 1) {
        skipPreviewReloadRef.current = false;
        await loadPreview();
      }
    } finally {
      setPageLoading(false);
      skipCatalogReloadRef.current = false;
    }
  }, [loadBaseData, loadCatalog, loadPreview, tab]);

  const refreshAfterImport = useCallback(async () => {
    skipCatalogReloadRef.current = true;
    skipPreviewReloadRef.current = false;
    try {
      const currentVendor = await loadVendor();
      await loadMapping(currentVendor);
      await Promise.all([loadPreview(), loadCatalog()]);
    } catch (error) {
      setServiceError(error.message || "Errore nell'aggiornamento post-import");
    } finally {
      skipCatalogReloadRef.current = false;
    }
  }, [loadVendor, loadMapping, loadPreview, loadCatalog]);

  const {
    importRunning,
    scheduledImportActive,
    maintenanceLocked,
    progressActive,
    stoppingImport,
    jobStatus,
    logLines,
    logDialogOpen,
    logContainerRef,
    hasLogs,
    startJob,
    stopJob,
    openLogs,
    closeLogs,
    clearLogs,
    runTrackedAction,
    formatActivitySeparator,
  } = useCatalogImportJob({
    onComplete: async () => {
      await refreshAfterImport();
      startVendorIndexPolling();
    },
    onError: (error) => showError(error.message),
  });

  const vendorReindexActive = reindexSubmitting || isVendorReindexRunning(vendorId);

  const handleReindex = async () => {
    if (vendorReindexActive || maintenanceLocked) {
      return;
    }
    setReindexSubmitting(true);
    try {
      await triggerVendorReindex(vendorId);
      showSuccess("Reindex vendor avviato.");
    } catch (error) {
      showError(error.message || "Errore avvio reindex vendor");
      setReindexSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isVendorReindexRunning(vendorId)) {
      setReindexSubmitting(false);
    }
  }, [isVendorReindexRunning, vendorId]);

  const operationsDisabled = importRunning || maintenanceLocked;

  useEffect(() => {
    loadAll();
    skipPreviewReloadRef.current = true;
    setPreview(null);
    setPreviewError("");
  }, [vendorId]);

  useEffect(() => {
    if (skipCatalogReloadRef.current) {
      return;
    }
    loadCatalog();
  }, [page, rowsPerPage, catalogFilters, hardDeleteRules, hardShowMatchesOnly, vendorId, loadCatalog]);

  useEffect(() => {
    if (tab !== 1) {
      return;
    }
    if (skipPreviewReloadRef.current) {
      skipPreviewReloadRef.current = false;
    }
    loadPreview();
  }, [tab, mappingPage, mappingRowsPerPage, mappingProblemFilter, skipShowMatchesOnly, defShowMatchesOnly, loadPreview]);

  const handleHardShowMatchesOnlyChange = (value) => {
    setHardShowMatchesOnly(value);
    setPage(0);
  };

  const handleCatalogFilterChange = (field, value) => {
    if (field === "reset") {
      setCatalogFilters({
        uncategorizedOnly: false,
        isDisattivato: "",
        isDeleted: "",
        certificato: "",
        stato: "",
        productCodeFilter: "",
        columnFilters: defaultCatalogColumnFilters(),
      });
    } else if (field === "columnFilterAdd") {
      setCatalogFilters((previous) => ({
        ...previous,
        columnFilters: [...(previous.columnFilters || []), createEmptyColumnFilter()],
      }));
    } else if (field === "columnFilterRemove") {
      setCatalogFilters((previous) => {
        const nextFilters = (previous.columnFilters || []).filter((filter) => filter.id !== value);
        return {
          ...previous,
          columnFilters: nextFilters.length > 0 ? nextFilters : defaultCatalogColumnFilters(),
        };
      });
    } else if (field === "columnFilterUpdate") {
      const { id, filterField, filterValue } = value || {};
      setCatalogFilters((previous) => ({
        ...previous,
        columnFilters: (previous.columnFilters || []).map((filter) => (
          filter.id === id ? { ...filter, [filterField]: filterValue } : filter
        )),
      }));
    } else {
      setCatalogFilters((previous) => ({ ...previous, [field]: value }));
    }
    setPage(0);
  };

  const handleMappingChange = (column, value) => {
    if (value !== "skip") {
      const duplicateColumn = Object.entries(mapping).find(([col, field]) => field === value && col !== column);
      if (duplicateColumn) {
        showError(`Il campo '${value}' e gia mappato sulla colonna ${duplicateColumn[0]}`);
        return;
      }
    }
    setMapping((prev) => ({ ...prev, [column]: value }));
  };

  const buildSavePayload = useCallback((overrides = {}) => ({
    feed_config: withManualDetection(sourceTypeConfig),
    column_mapping: mapping,
    mapping_config: buildMappingRulesPayload(
      mapping,
      rowSkipRules,
      preview?.column_count || columnNumbers.length,
      {
        scheduledEnabled: overrides.scheduledEnabled ?? scheduledEnabled,
        columnDefaults: overrides.columnDefaults ?? columnDefaults,
      },
    ),
    row_skip: rowSkipRules,
  }), [
    sourceTypeConfig,
    mapping,
    rowSkipRules,
    preview?.column_count,
    columnNumbers.length,
    scheduledEnabled,
    columnDefaults,
  ]);

  const persistMappingProfile = useCallback(async (overrides = {}) => {
    const saved = await saveCatalogMapping(vendorId, buildSavePayload(overrides));
    applyMappingData(saved, vendor);
    return saved;
  }, [vendorId, buildSavePayload, applyMappingData, vendor]);

  const handleSaveRawMetadata = useCallback(async ({ feed_config: nextFeedConfig, mapping_config: nextMappingConfig }) => {
    setSavingRawMetadata(true);
    try {
      const saved = await saveCatalogMapping(vendorId, {
        feed_config: nextFeedConfig,
        mapping_config: nextMappingConfig,
      });
      applyMappingData(saved, vendor);
    } finally {
      setSavingRawMetadata(false);
    }
  }, [vendorId, applyMappingData, vendor]);

  const handleScheduledEnabledChange = async (enabled) => {
    const previous = scheduledEnabled;
    setScheduledEnabled(enabled);
    setPersistingConfig(true);
    try {
      await persistMappingProfile({ scheduledEnabled: enabled });
    } catch (error) {
      setScheduledEnabled(previous);
      showError(error.message);
    } finally {
      setPersistingConfig(false);
    }
  };

  const handleColumnDefaultsChange = async (nextRules) => {
    const previous = columnDefaults;
    const normalized = normalizeColumnDefaultGroups(nextRules);
    setColumnDefaults(normalized);
    setPersistingConfig(true);
    try {
      await persistMappingProfile({ columnDefaults: normalized });
    } catch (error) {
      setColumnDefaults(previous);
      showError(error.message);
    } finally {
      setPersistingConfig(false);
    }
  };

  const handleSkipShowMatchesOnlyChange = (value) => {
    setSkipShowMatchesOnly(value);
    setMappingPage(0);
  };

  const handleDefShowMatchesOnlyChange = (value) => {
    setDefShowMatchesOnly(value);
    setMappingPage(0);
  };

  const handleSaveMapping = async () => {
    setSavingMapping(true);
    try {
      const saved = await saveCatalogMapping(vendorId, buildSavePayload());
      applyMappingData(saved, vendor);
    } catch (error) {
      showError(error.message);
    } finally {
      setSavingMapping(false);
    }
  };

  const handleSaveSourceType = async () => {
    const saved = await saveCatalogMapping(vendorId, buildSavePayload());
    applyMappingData(saved, vendor);
  };

  const handleImport = async () => {
    try {
      await startJob([Number(vendorId)], { importType: "manual_single" });
    } catch (error) {
      showError(error.message);
    }
  };

  const handleImportFromStored = async () => {
    setSavingMapping(true);
    try {
      const saved = await saveCatalogMapping(vendorId, buildSavePayload());
      applyMappingData(saved, vendor);
      await startJob([Number(vendorId)], { useStoredFeed: true, importType: "manual_stored" });
    } catch (error) {
      showError(error.message);
    } finally {
      setSavingMapping(false);
    }
  };

  const handleMappingCellSave = async ({ row_index: rowIndex, column, value }) => {
    try {
      await saveNormalizedCell(vendorId, { row_index: rowIndex, column, value });
      await loadPreview();
    } catch (error) {
      showError(error.message);
      throw error;
    }
  };

  const handleDeleteNormalizedRow = async (rowIndex) => {
    try {
      await deleteNormalizedRow(vendorId, rowIndex);
      setMappingProblemFilter(MAPPING_PROBLEM_FILTER.ALL);
      await Promise.all([loadPreview(), loadMapping()]);
    } catch (error) {
      showError(error.message);
    }
  };

  const handleMappingGoToLine = (lineNumber) => {
    const total = preview?.total_rows || 0;
    if (lineNumber < 1 || lineNumber > total) {
      showError(`Riga non valida: inserire un numero tra 1 e ${total}.`);
      return;
    }
    const targetPage = Math.floor((lineNumber - 1) / mappingRowsPerPage);
    setScrollTargetFeedRow(lineNumber);
    if (targetPage !== mappingPage) {
      setMappingPage(targetPage);
    }
  };

  const importProblemsAvailable = useMemo(
    () => mappingImportProblemsAvailable(
      mappingMeta.last_run_summary,
      mappingMeta.import_errors_available,
    ),
    [mappingMeta.import_errors_available, mappingMeta.last_run_summary],
  );

  const handleToggleImportProblems = () => {
    if (!importProblemsAvailable) {
      return;
    }
    setMappingProblemFilter((previous) => cycleMappingProblemFilter(previous));
    setMappingPage(0);
  };

  const handleCellSave = async ({
    table,
    rowIndex,
    column,
    value,
    catalogListId,
    productListId,
  }) => {
    if (!canCatalogo) {
      return;
    }
    try {
      await saveCatalogCell(vendorId, {
        table,
        row_index: rowIndex,
        column,
        value,
        catalog_list_id: catalogListId,
        product_list_id: productListId,
      });
      await loadCatalog();
    } catch (error) {
      showError(error.message);
      throw error;
    }
  };

  const handleBulkCategorizeExecuteAll = async (rules) => {
    if (!canBulkCategorize || !rules?.length) {
      return;
    }
    setExecutingBulkCategorize(true);
    try {
      const result = await bulkCategorizeByAlbero(vendorId, { mappings: rules });
      setBulkCategorizeRules([]);
      await loadCatalog();
      if (result?.updated_count === 0) {
        showError("Nessun prodotto trovato per gli abbinamenti BULK CAT configurati");
      }
    } catch (error) {
      showError(error.message);
    } finally {
      setExecutingBulkCategorize(false);
    }
  };

  const handleHardDeleteExecuteAll = async (rules) => {
    if (!canCatalogo || !rules?.length) {
      return;
    }
    setExecutingHardDelete(true);
    try {
      const result = await hardDeleteCatalogRows(vendorId, { rules });
      setHardDeleteRules([]);
      await loadCatalog();
      if (result?.deleted_count === 0) {
        showError(`Nessun catalogo trovato per ${formatHardDeleteRulesLabel(rules)}`);
      }
    } catch (error) {
      showError(error.message);
    } finally {
      setExecutingHardDelete(false);
    }
  };

  const handleNormalized = useCallback(async (normalizedPreview, detectedConfig) => {
    skipPreviewReloadRef.current = false;
    setPreview(normalizedPreview);
    setPreviewError("");
    setSourceTypeConfig(parseSourceTypeConfig(detectedConfig, vendor));
    setMappingProblemFilter(MAPPING_PROBLEM_FILTER.ALL);
    setMappingPage(0);
    setTab(1);

    const previewHasMapping = Boolean(
      normalizedPreview?.column_mapping
      && Object.keys(normalizedPreview.column_mapping).length > 0,
    );

    if (previewHasMapping) {
      applyMappingData({
        column_mapping: normalizedPreview.column_mapping,
        feed_config: detectedConfig ?? normalizedPreview.feed_config,
        mapping_status: normalizedPreview.mapping_status || "auto",
        issues: normalizedPreview.issues || [],
        warnings: normalizedPreview.warnings || [],
        import_errors_available: false,
      }, vendor);
      return;
    }

    const mappingData = await loadMapping();
    if (mappingData) {
      applyMappingData({
        ...mappingData,
        import_errors_available: false,
      }, vendor);
    }
  }, [loadMapping, applyMappingData, vendor]);

  const mappingTabStatus = useMemo(
    () => resolveMappingTabStatus({
      previewError,
      previewLoading,
      preview,
    }),
    [previewError, previewLoading, preview],
  );

  const catalogTabStatus = useMemo(() => {
    if (catalogLoadError) {
      return { severity: "warning", text: catalogLoadError };
    }
    if (catalogLoading) {
      return null;
    }
    if (!(catalogPage.total > 0)) {
      return {
        severity: "info",
        text: "Nessun prodotto trovato per questo vendor.",
      };
    }
    return null;
  }, [catalogLoadError, catalogLoading, catalogPage.total]);

  return (
    <CatalogMaintenanceShell>
      <div className="cm-stack-header">
        <div className="cm-stack-header-main">
          <Typography className="cm-title cm-title-with-vendor-names" component="h1">
            {buildVendorBusinessTradeTitle(vendorId, vendor)}
          </Typography>
          <ScheduledImportToggle
            enabled={scheduledEnabled}
            saving={persistingConfig}
            disabled={maintenanceLocked || !canVendorSchedule}
            onChange={handleScheduledEnabledChange}
          />
        </div>
        <div className="cm-actions">
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} title={CM_TOOLTIPS.tornaIndietro}>
            Torna indietro
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadAll} disabled={pageLoading || maintenanceLocked} title={CM_TOOLTIPS.ricaricaVendor}>
            Ricarica
          </Button>
          <Button variant="outlined" startIcon={<TerminalIcon />} onClick={openLogs} disabled={!hasLogs && !scheduledImportActive} title={CM_TOOLTIPS.logs}>
            Logs
          </Button>
          <Button
            variant="outlined"
            startIcon={<SyncIcon />}
            onClick={handleReindex}
            disabled={operationsDisabled || vendorReindexActive}
            title={CM_TOOLTIPS.reindexVendor}
          >
            {vendorReindexActive ? "Reindex in corso..." : "Reindex"}
          </Button>
          <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={handleImport} disabled={operationsDisabled || !canImport} title={CM_TOOLTIPS.importaVendor}>
            Importa
          </Button>
        </div>
      </div>

      <CatalogVendorSummary
        sourceTypeConfig={sourceTypeConfig}
        mappingMeta={mappingMeta}
        vendor={vendor}
        issues={issues}
        warnings={warnings}
        onOpenMappingLegend={() => setMappingLegendOpen(true)}
        onOpenImportLegend={() => setImportLegendOpen(true)}
        onOpenValidationLegend={() => setValidationLegendOpen(true)}
        onOpenImportIssuesLegend={() => setImportIssuesLegendOpen(true)}
        onOpenGlobalImportLegend={() => setGlobalImportLegendOpen(true)}
        onOpenFormatLegend={() => setFormatLegendOpen(true)}
      />

      {serviceError && (
        <Alert className="cm-spacer-md" severity="warning">{serviceError}</Alert>
      )}

      {scheduledImportActive && (
        <Alert className="cm-spacer-md" severity="warning" action={(
          <Button variant="text" size="small" onClick={() => navigate("/manutenzione-cataloghi")}>
            Vai alla lista
          </Button>
        )}>
          Import schedulato in corso: operazioni disabilitate. Arresta il job dalla toolbar Manutenzione Cataloghi.
          {jobStatus?.processed != null && jobStatus?.total != null ? ` (${jobStatus.processed}/${jobStatus.total})` : ""}
        </Alert>
      )}

      {importRunning && !scheduledImportActive && (
        <Alert className="cm-spacer-md" severity="info" action={(
          <Button variant="text" size="small" onClick={openLogs} title={CM_TOOLTIPS.mostraLog}>
            Mostra log
          </Button>
        )}>
          Import in corso per vendor {vendorId}
          {jobStatus?.current_vendor_name ? ` — ${jobStatus.current_vendor_name}` : ""}
        </Alert>
      )}

      {vendorReindexActive && !importRunning && (
        <Alert className="cm-spacer-md" severity="info">
          Reindex in corso per vendor {vendorId}. I prodotti verranno aggiornati in Elasticsearch al termine.
        </Alert>
      )}

      <div className={maintenanceLocked ? "cm-maintenance-locked" : undefined}>
      <Tabs value={tab} onChange={(_, value) => setTab(value)} aria-disabled={maintenanceLocked}>
        <Tab label="Acquisizione feed" />
        <Tab label="Revisione e Mapping" />
        <Tab label="Rigenera Codice Prodotto" />
        <Tab label="Catalogo e Categorizzazione" />
        <Tab label="Raw Metadata" />
      </Tabs>

      <div className="cm-tab-panel" hidden={tab !== 0}>
        <RawFeedTab
          vendorId={vendorId}
          feedUrl={vendor?.feed_url || ""}
          isActive={tab === 0}
          sourceTypeConfig={sourceTypeConfig}
          onSourceTypeConfigChange={setSourceTypeConfig}
          onSave={canMapping ? handleSaveSourceType : null}
          onNormalized={handleNormalized}
          showError={showError}
          runTrackedAction={runTrackedAction}
          formatActivitySeparator={formatActivitySeparator}
          mappingReadOnly={!canMapping || maintenanceLocked}
        />
      </div>

      <div className="cm-tab-panel" hidden={tab !== 1}>
        <Paper className="cm-panel">
          <div className="cm-tab-toolbar cm-mapping-tab-toolbar">
            <div className="cm-tab-toolbar-left-stack">
              <RowSkipRulesPanel
                columnNumbers={columnNumbers}
                headerLabels={preview?.header_labels}
                isHeadless={sourceTypeConfig.is_headless}
                rules={rowSkipRules}
                disabled={!canMapping || maintenanceLocked || persistingConfig}
                onChange={(nextRules) => setRowSkipRules(normalizeRowSkipRules(nextRules))}
                showMatchesOnly={skipShowMatchesOnly}
                onShowMatchesOnlyChange={handleSkipShowMatchesOnlyChange}
              />
              <ColumnDefaultsPanel
                columnDefaults={columnDefaults}
                fieldKeys={fieldKeys}
                columnNumbers={columnNumbers}
                headerLabels={preview?.header_labels}
                isHeadless={sourceTypeConfig.is_headless}
                mapping={mapping}
                disabled={!canMapping || maintenanceLocked || persistingConfig}
                onColumnDefaultsChange={handleColumnDefaultsChange}
                showMatchesOnly={defShowMatchesOnly}
                onShowMatchesOnlyChange={handleDefShowMatchesOnlyChange}
              />
            </div>
            <div className="cm-tab-toolbar-right">
              <div className="cm-tab-toolbar-right-tools">
                <button
                  type="button"
                  className="cm-btn cm-tab-toolbar-info-btn"
                  onClick={() => setLegendOpen(true)}
                  title={CM_TOOLTIPS.legendaMapping}
                >
                  [i]
                </button>
                <GoToLinePanel
                  totalLines={preview?.total_rows || 0}
                  onGoToLine={handleMappingGoToLine}
                  disabled={previewLoading || !(preview?.total_rows > 0)}
                />
              </div>
              <div className="cm-tab-toolbar-actions">
                <button
                  type="button"
                  className={`cm-btn cm-btn-outlined${mappingProblemFilterIsActive(mappingProblemFilter) ? " is-active" : ""}`}
                  onClick={handleToggleImportProblems}
                  disabled={!importProblemsAvailable}
                  title={mappingProblemFilterTooltip(mappingProblemFilter, CM_TOOLTIPS)}
                >
                  {mappingProblemFilterButtonLabel(mappingProblemFilter)}
                </button>
              <button
                type="button"
                className="cm-btn cm-btn-primary"
                onClick={handleSaveMapping}
                disabled={savingMapping || !canMapping || maintenanceLocked}
                title={CM_TOOLTIPS.salvaMapping}
              >
                {savingMapping ? "Salva..." : "Salva mapping"}
              </button>
              <button
                type="button"
                className="cm-btn cm-btn-primary"
                onClick={handleImportFromStored}
                disabled={importRunning || savingMapping || !preview?.total_rows || issues.length > 0 || !canImport || !canMapping || maintenanceLocked}
                title={CM_TOOLTIPS.importaDaNormalizzato}
              >
                {savingMapping ? "Salva..." : "Importa"}
              </button>
              </div>
            </div>
          </div>

          <CatalogTabStatusMessage
            message={mappingTabStatus?.text}
            severity={mappingTabStatus?.severity}
          />

          <MappingTokenStatusBar
            fieldKeys={fieldKeys}
            mapping={mapping}
            columnDefaultRules={columnDefaults}
          />

          <MappingPreviewMatrix
            columnNumbers={columnNumbers}
            headerLabels={preview?.header_labels}
            previewRows={previewRows}
            mapping={mapping}
            fieldKeys={fieldKeys}
            onMappingChange={handleMappingChange}
            pageOffset={mappingPage * mappingRowsPerPage}
            showErrorColumn={mappingProblemFilterIsActive(mappingProblemFilter)}
            rowErrors={mappingMeta.last_run_summary?.row_errors}
            rowWarnings={mappingMeta.last_run_summary?.row_warnings}
            importData={mappingMeta.last_run_summary}
            onCellSave={canMapping ? handleMappingCellSave : null}
            onDeleteRow={canMapping ? handleDeleteNormalizedRow : null}
            isHeadless={sourceTypeConfig.is_headless}
            loading={previewLoading}
            rowSkipRules={rowSkipRules}
            showSkipMatchesOnly={skipShowMatchesOnly}
            columnDefaultRules={columnDefaults}
            showColumnDefaultOverlays
            scrollTargetFeedRow={scrollTargetFeedRow}
            onScrollTargetHandled={() => setScrollTargetFeedRow(null)}
            readOnly={!canMapping || maintenanceLocked}
          />
          <CatalogTablePagination
            rowsPerPage={mappingRowsPerPage}
            setRowsPerPage={(value) => {
              setMappingRowsPerPage(value);
              setMappingPage(0);
            }}
            page={mappingPage}
            setPage={setMappingPage}
            totalPages={totalMappingPages}
            countLabel="Righe feed"
            countValue={preview?.total_rows || 0}
            rowsOptions={[20, 50, 100, 200]}
          />
        </Paper>
      </div>

      <div className="cm-tab-panel" hidden={tab !== 2}>
        <ProductCodeRegenPanel
          vendorId={vendorId}
          fieldKeys={fieldKeys}
          disabled={maintenanceLocked || !canProductCodeRegen}
          showError={showError}
        />
      </div>

      <div className="cm-tab-panel" hidden={tab !== 3}>
        <Paper className="cm-panel">
          <CatalogDbOutputView
            rows={catalogPage.rows || []}
            productColumns={catalogPage.product_columns}
            catalogColumns={catalogPage.catalog_columns}
            pageOffset={page * rowsPerPage}
            catalogFilters={catalogFilters}
            onCatalogFilterChange={handleCatalogFilterChange}
            onCellSave={canCatalogo ? handleCellSave : null}
            tableLoading={catalogLoading}
            statusMessage={catalogTabStatus?.text}
            statusSeverity={catalogTabStatus?.severity}
            readOnly={!canCatalogo || maintenanceLocked}
            bulkCategorizeRules={bulkCategorizeRules}
            onBulkCategorizeRulesChange={setBulkCategorizeRules}
            onBulkCategorizeExecuteAll={handleBulkCategorizeExecuteAll}
            executingBulkCategorize={executingBulkCategorize}
            bulkCategorizeDisabled={!canBulkCategorize || maintenanceLocked || catalogLoading}
            hardDeleteRules={hardDeleteRules}
            onHardDeleteRulesChange={setHardDeleteRules}
            onHardDeleteExecuteAll={handleHardDeleteExecuteAll}
            executingHardDelete={executingHardDelete}
            hardDeleteDisabled={!canCatalogo || maintenanceLocked || catalogLoading}
            hardShowMatchesOnly={hardShowMatchesOnly}
            onHardShowMatchesOnlyChange={handleHardShowMatchesOnlyChange}
          />
          <CatalogTablePagination
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            page={page}
            setPage={setPage}
            totalPages={totalCatalogPages}
            countLabel="Prodotti catalogo"
            countValue={catalogPage.total || 0}
            rowsOptions={[10, 25, 50, 100]}
          />
        </Paper>
      </div>

      <div className="cm-tab-panel" hidden={tab !== 4}>
        <RawMetadataTab
          feedConfig={rawMetadata.feed_config}
          mappingConfig={rawMetadata.mapping_config}
          lastRunSummary={rawMetadata.last_run_summary}
          onSaveMetadata={handleSaveRawMetadata}
          saving={savingRawMetadata}
        />
      </div>

      </div>

      <LegendDialog
        open={legendOpen}
        onClose={() => setLegendOpen(false)}
        sections={[
          {
            title: MAPPING_TOKEN_LEGEND_TITLE,
            items: buildMappingLegendItems(fieldKeys),
          },
          {
            title: MAPPING_VALIDATION_LEGEND_TITLE,
            items: MAPPING_VALIDATION_LEGEND,
          },
        ]}
      />

      <LegendDialog
        open={mappingLegendOpen}
        onClose={() => setMappingLegendOpen(false)}
        title={MAPPING_STATE_LEGEND_TITLE}
        items={MAPPING_STATE_LEGEND}
      />

      <LegendDialog
        open={importLegendOpen}
        onClose={() => setImportLegendOpen(false)}
        title={IMPORT_STATUS_LEGEND_TITLE}
        items={IMPORT_STATUS_LEGEND}
      />

      <LegendDialog
        open={validationLegendOpen}
        onClose={() => setValidationLegendOpen(false)}
        title={MAPPING_VALIDATION_LEGEND_TITLE}
        items={MAPPING_VALIDATION_LEGEND}
      />

      <LegendDialog
        open={importIssuesLegendOpen}
        onClose={() => setImportIssuesLegendOpen(false)}
        title={IMPORT_ISSUES_LEGEND_TITLE}
        items={IMPORT_ISSUES_LEGEND}
      />

      <LegendDialog
        open={globalImportLegendOpen}
        onClose={() => setGlobalImportLegendOpen(false)}
        sections={[
          {
            title: "Errori globali import",
            items: GLOBAL_IMPORT_ERRORS_LEGEND,
          },
          {
            title: "Warning globali import",
            items: GLOBAL_IMPORT_WARNINGS_LEGEND,
          },
        ]}
      />

      <LegendDialog
        open={formatLegendOpen}
        onClose={() => setFormatLegendOpen(false)}
        title={FEED_FORMAT_LEGEND_TITLE}
        items={FEED_FORMAT_LEGEND}
      />

      <CatalogImportLogDialog
        open={logDialogOpen}
        onClose={closeLogs}
        onClearLogs={clearLogs}
        onStopImport={maintenanceLocked ? undefined : stopJob}
        stoppingImport={stoppingImport}
        logLines={logLines}
        logContainerRef={logContainerRef}
        jobStatus={jobStatus}
        importRunning={progressActive}
        title={`Log import vendor ${vendorId}`}
      />
    </CatalogMaintenanceShell>
  );
};

export default CatalogMappingEditor;
